import { Controller, type Control, useWatch } from 'react-hook-form';
import { Box, FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import { COUNTRIES, COUNTRY_TO_CODE_MAP } from '@/constants';
import { useTranslations } from '@/i18n/use-translations';
import { type TranslationKey } from '@/i18n/translation-key';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useRef, useState } from 'react';
import { VerifiedOutlined } from '@mui/icons-material';
import { useVatValidation } from './use-vat-validation.hook';
import type { EditActiveOrganization } from '../../edit.modal';
import { useTaxClassificationMutation } from '@/state/mutations/vat-validation/use-tax-classification';
import type { SetValueFunction, ClearErrorsFunction } from '@/types/form';

export default function BillingForm({
  control,
  setValue,
  clearErrors,
}: {
  control: Control<EditActiveOrganization>;
  setValue: SetValueFunction<EditActiveOrganization>;
  clearErrors: ClearErrorsFunction<EditActiveOrganization>;
}) {
  const t = useTranslations();
  const hintRef = useRef<HTMLParagraphElement>(null);
  const [vatAllowed, setVatAllowed] = useState(true);
  const [wasDirty, setWasDirty] = useState(false);

  const vatValue = useWatch({
    control,
    name: 'billingAddress.vat',
    defaultValue: '',
  });
  const country = useWatch({
    control,
    name: 'billingAddress.country',
    defaultValue: '',
  });

  // Set wasDirty to true when VAT value first appears (render-time update)
  const [prevVatValue, setPrevVatValue] = useState(vatValue);
  if (prevVatValue !== vatValue && vatValue && !wasDirty) {
    setPrevVatValue(vatValue);
    setWasDirty(true);
  }

  // country scroll to hint {{{
  const selectedCountry = useWatch({
    control,
    name: 'billingAddress.country',
    defaultValue: '',
  });
  useEffect(() => {
    if (selectedCountry && selectedCountry !== COUNTRIES.germany && hintRef.current) {
      hintRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedCountry]);
  // }}}

  const { vatVerifiedColor } = useVatValidation({ control, clearErrors, wasDirty });
  const { mutateAsync: taxClassificationMutation } = useTaxClassificationMutation();

  const countryCode = selectedCountry
    ? COUNTRY_TO_CODE_MAP[selectedCountry as keyof typeof COUNTRY_TO_CODE_MAP] || ''
    : '';

  useEffect(() => {
    if (!countryCode) {
      setVatAllowed(true);
      return;
    }
    (async () => {
      try {
        const result = await taxClassificationMutation({
          countryCode,
        });
        const vatAllowed = result.taxClassification !== 'thirdPartyCountry';
        setVatAllowed(vatAllowed);
        if (!vatAllowed) {
          setValue('billingAddress.vat', '');
        }
      } catch {
        setVatAllowed(true);
      }
    })();
  }, [country, countryCode, setValue, taxClassificationMutation]);

  const sortedCountries = useMemo(() => {
    return Object.values(COUNTRIES).sort((a, b) => {
      const aTranslated = t(`countries.${a}` as TranslationKey);
      const bTranslated = t(`countries.${b}` as TranslationKey);
      return aTranslated.localeCompare(bTranslated);
    });
  }, [t]);

  return (
    <>
      <Box
        sx={{
          'display': 'flex',
          'flexDirection': 'column',
          'width': '100%',
          'maxWidth': '400px',
          '& .MuiFormControl-root': {
            mb: 1,
          },
        }}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>
          {t('organization.billingAddress')}
        </Typography>
        <Controller
          name="billingAddress.name"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              type="text"
              required
              data-testid="addressName"
              variant="standard"
              label={t('organization.addressName')}
              helperText={fieldState.error ? fieldState.error.message : null}
              error={!!fieldState.error}
            />
          )}
        />

        <Controller
          name="billingAddress.address1"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              type="text"
              required
              data-testid="address1"
              variant="standard"
              label={t('organization.address1')}
              helperText={fieldState.error ? fieldState.error.message : null}
              error={!!fieldState.error}
            />
          )}
        />

        <Controller
          name="billingAddress.address2"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              type="text"
              variant="standard"
              data-testid="address2"
              label={t('organization.address2')}
              helperText={fieldState.error ? fieldState.error.message : null}
              error={!!fieldState.error}
            />
          )}
        />
        <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
          <Controller
            name="billingAddress.postCode"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                sx={{ flex: 1 }}
                type="text"
                variant="standard"
                required
                data-testid="postCode"
                label={t('organization.postalCode')}
                helperText={fieldState.error ? fieldState.error.message : null}
                error={!!fieldState.error}
              />
            )}
          />
          <Controller
            name="billingAddress.city"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                sx={{ flex: 2 }}
                type="text"
                variant="standard"
                required
                data-testid="city"
                label={t('organization.city')}
                helperText={fieldState.error ? fieldState.error.message : null}
                error={!!fieldState.error}
              />
            )}
          />
        </Stack>
        <Controller
          name="billingAddress.country"
          control={control}
          render={({ field, fieldState }) => (
            <FormControl sx={{ width: '100%', mt: 1 }} error={!!fieldState.error}>
              <InputLabel>{`${t('organization.country')} *`}</InputLabel>
              <Select
                {...field}
                label={t('organization.country')}
                data-testid="country"
                variant="standard"
                renderValue={(value) => t(`countries.${value}` as TranslationKey)}
              >
                <MenuItem value="">
                  <em>{t('common.none')}</em>
                </MenuItem>
                {sortedCountries.map((country) => (
                  <MenuItem data-testid={country} key={country} value={country}>
                    {t(`countries.${country}` as TranslationKey)}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{fieldState.error ? fieldState.error.message : null}</FormHelperText>
            </FormControl>
          )}
        />

        {vatAllowed && (
          <Stack direction="row" spacing={2} width="100%">
            <TextField
              type="text"
              variant="standard"
              disabled
              value={countryCode}
              label=" " // NOTE: this space is required to make sure this input is in line with the vat input
              sx={{ width: '30px' }}
            />
            <Controller
              name="billingAddress.vat"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="text"
                  data-testid="vat"
                  variant="standard"
                  label={t('organization.vat')}
                  helperText={fieldState.error ? fieldState.error.message : null}
                  error={!!fieldState.error}
                  disabled={!selectedCountry}
                />
              )}
            />
            <Box>
              <VerifiedOutlined sx={{ mt: '15px', color: vatVerifiedColor }} />
            </Box>
          </Stack>
        )}
      </Box>
      {selectedCountry !== COUNTRIES.germany && vatAllowed && (
        <Typography ref={hintRef} variant="body2">
          {t('organization.vatIdHint')}
        </Typography>
      )}
    </>
  );
}
